import classNames from "classnames";
import React, {
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
} from "react";

interface TextAreaProps {
  id?: string;
  name?: string;
  rows?: number;
  value?: string;
  label?: string | JSX.Element;
  required?: boolean;
  error?: string | JSX.Element;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  disabled?: boolean;
  dataCy?: string;
  placeholder?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      id,
      name,
      value,
      rows = 6,
      label,
      error,
      required,
      onChange,
      onBlur,
      disabled = false,
      dataCy,
      placeholder,
      ...props
    }: TextAreaProps,
    ref
  ) => {
    return (
      <div className="min-w-0 w-full font-sans">
        {label && (
          <div
            className={classNames(
              "mb-1.5 flex items-center gap-2 text-sm font-medium",
              disabled ? "opacity-50" : "opacity-100",
              "text-primary-700 dark:text-primary-300"
            )}
          >
            {label}
            {required && (
              <span className="ml-1 text-error-500 dark:text-error-400">*</span>
            )}
          </div>
        )}
        <div className="relative flex min-w-0">
          <textarea
            id={id}
            ref={ref}
            name={name}
            required={required}
            disabled={disabled}
            onChange={onChange}
            onBlur={onBlur}
            rows={rows}
            value={value}
            placeholder={placeholder}
            data-cy={dataCy}
            {...props}
            className={classNames(
              "min-h-20 min-w-0 flex-1 rounded-lg px-4 py-2 text-sm transition-all",
              "border border-solid font-sans",
              // Base gradient and shadow
              "bg-white dark:bg-transparent bg-gradient-to-b from-white to-primary-50/50 dark:from-content-900 dark:to-primary-950/70",
              "text-primary-700 dark:text-content-100",
              "border-primary-200/80 dark:border-primary-800/70",
              "shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)]",
              "dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.24)]",
              // Hover state
              "hover:border-primary-300 dark:hover:border-primary-700",
              "hover:shadow-[inset_0_1px_3px_rgba(0,0,0,0.1)]",
              "dark:hover:shadow-[inset_0_1px_3px_rgba(0,0,0,0.28)]",
              // Focus state
              "focus:border-primary-400 dark:focus:border-primary-400",
              "focus:shadow-[inset_0_1px_2px_rgba(0,0,0,0.06),0_0_0_3px_rgba(167,80,53,0.16)]",
              "dark:focus:shadow-[inset_0_1px_2px_rgba(0,0,0,0.24),0_0_0_3px_rgba(167,80,53,0.28)]",
              "focus:outline-none",
              // Placeholder
              "placeholder:text-primary-400 dark:placeholder:text-content-300",
              // Disabled state
              disabled &&
                "cursor-not-allowed opacity-50 bg-primary-50/50 dark:bg-primary-900/30 hover:shadow-none dark:hover:shadow-none",
              // Error state
              error &&
                "border-error-300 dark:border-error-700 shadow-[inset_0_1px_2px_rgba(220,38,38,0.1)] dark:shadow-[inset_0_1px_2px_rgba(220,38,38,0.2)]"
            )}
          />
        </div>
        {error && (
          <p className="mt-1.5 text-sm font-medium text-error-600 dark:text-error-400">
            {error}
          </p>
        )}
      </div>
    );
  }
);

TextArea.displayName = "TextArea";
